/**
 * contractAcceptanceStats — 22/07/2026 (retour user prod).
 * « Je préférerais voir le nombre de contrats honorés versus ceux que
 * j'ai laissé filer par catégorie de revenus (< 500 €, 500-1 000 €,
 * 1 000-2 000 €, > 2 000 €). »
 *
 * Construit à partir du contractLedger (append-only, cap 2 000 entrées).
 * « Honoré » = le joueur a pris le contrat (accepted/completed/cancelled/
 * displaced). « Laissé filer » = expiré sans acceptation. Les annonces
 * encore en cours (proposed) sont ignorées.
 */

export const REVENUE_BUCKETS = [
  { id: 'lt500', label: '< 500 €', min: 0, max: 500 },
  { id: 'b500', label: '500 – 1 000 €', min: 500, max: 1000 },
  { id: 'b1000', label: '1 000 – 2 000 €', min: 1000, max: 2000 },
  { id: 'gt2000', label: '> 2 000 €', min: 2000, max: Infinity },
];

const TAKEN_STATUSES = new Set(['accepted', 'completed', 'cancelled', 'displaced']);

function entryValue(e) {
  if (e.frequency === 'ponctuel') return e.acceptedRevenue || e.revenue || 0;
  return e.totalValue || ((e.dailyRevenue || 0) * (e.duration || 0));
}

/**
 * Stats d'acceptation par tranche de valeur sur la fenêtre annuelle
 * se terminant à `dayEnd`. Retourne null si aucune donnée.
 */
export function buildContractAcceptanceStats(state, dayEnd) {
  const ledger = state.contractLedger || [];
  if (ledger.length === 0) return null;
  const dayStart = Math.max(0, (dayEnd || 0) - 360);
  const inYear = ledger.filter(e => (e.spawnDay || 0) > dayStart && (e.spawnDay || 0) <= dayEnd);
  if (inYear.length === 0) return null;

  const rows = REVENUE_BUCKETS.map(b => ({ ...b, taken: 0, missed: 0, entries: [] }));
  let totalTaken = 0;
  let totalMissed = 0;
  let coverageFromDay = Infinity;
  // 25/07/2026 (retour user) — drill-down : détail par contrat (catégorie,
  // hors zone) + agrégat par catégorie (Frigo/BTP/Export…).
  const catMap = {};
  for (const e of inYear) {
    coverageFromDay = Math.min(coverageFromDay, e.spawnDay || 0);
    const value = entryValue(e);
    const row = rows.find(r => value >= r.min && value < r.max) || rows[rows.length - 1];
    const isTaken = TAKEN_STATUSES.has(e.status);
    const isMissed = e.status === 'expired';
    if (!isTaken && !isMissed) continue;
    if (isTaken) {
      row.taken += 1;
      totalTaken += 1;
    } else {
      row.missed += 1;
      totalMissed += 1;
    }
    row.entries.push({
      name: e.name || e.contractId || 'Contrat',
      category: e.category || 'Autre',
      value,
      taken: isTaken,
      horsZone: !!e.horsZone,
      spawnDay: e.spawnDay || 0,
    });
    const catKey = e.category || 'Autre';
    const cat = catMap[catKey] || (catMap[catKey] = { category: catKey, taken: 0, missed: 0 });
    if (isTaken) cat.taken += 1; else cat.missed += 1;
  }
  // Détail borné : 40 contrats max par tranche, les plus récents d'abord.
  rows.forEach(r => {
    r.entries.sort((a, b) => (b.spawnDay || 0) - (a.spawnDay || 0));
    if (r.entries.length > 40) r.entries = r.entries.slice(0, 40);
  });
  const byCategory = Object.values(catMap)
    .map(c => ({
      ...c,
      rate: (c.taken + c.missed) > 0 ? Math.round((c.taken / (c.taken + c.missed)) * 100) : null,
    }))
    .sort((a, b) => (b.taken + b.missed) - (a.taken + a.missed));
  const decided = totalTaken + totalMissed;
  return {
    rows: rows.map(r => ({
      ...r,
      rate: (r.taken + r.missed) > 0 ? Math.round((r.taken / (r.taken + r.missed)) * 100) : null,
    })),
    byCategory,
    totalTaken,
    totalMissed,
    acceptanceRate: decided > 0 ? Math.round((totalTaken / decided) * 100) : null,
    coverageFromDay: coverageFromDay === Infinity ? null : coverageFromDay,
    // Le ledger est borné (2 000 entrées) : signaler quand il ne couvre
    // pas toute l'année écoulée.
    partial: coverageFromDay > dayStart + 1,
  };
}

/**
 * Panier moyen RÉEL des contrats livrés sur la fenêtre annuelle. Corrige
 * le « panier moyen à 453 268 € » (CA total divisé par un compteur de
 * contrats sous-évalué). Retourne null si aucun contrat livré tracé.
 */
export function computeAvgContractValue(state, dayEnd) {
  const ledger = state.contractLedger || [];
  const dayStart = Math.max(0, (dayEnd || 0) - 360);
  let sum = 0;
  let count = 0;
  for (const e of ledger) {
    if (e.status !== 'completed') continue;
    const d = e.completedOnDay || 0;
    if (d <= dayStart || d > dayEnd) continue;
    sum += e.frequency === 'ponctuel'
      ? (e.acceptedRevenue || e.revenue || 0)
      : ((e.acceptedDailyRevenue || e.dailyRevenue || 0) * (e.daysServed || e.duration || 0));
    count += 1;
  }
  return count > 0 ? Math.round(sum / count) : null;
}
